// Quest System Constants - Web2-Friendly Marketing Campaign Model
// "We let real users share your brand, visit your website, and talk about you — and you only pay for verified actions."

// ========== QUEST TYPES (5 Core Types) ==========
export const QUEST_TYPES = {
  SHARE: 'share',          // Share & Amplify - Bread and butter
  VISIT: 'visit',          // Visit & Engage - Underrated, valuable for Web2
  FOLLOW: 'follow',        // Follow / Subscribe - Classic, simple, effective
  REVIEW: 'review',        // Review & Feedback - High value for Web2 businesses
  REFERRAL: 'referral',    // Referral & Word-of-Mouth - Keep it simple and honest
};

export const QUEST_TYPE_CONFIG = {
  [QUEST_TYPES.SHARE]: {
    label: 'Share & Amplify',
    icon: '📢',
    description: 'Pay users to share your content',
    color: 'orange',
    examples: ['Share a page or link', 'Repost a campaign', 'Share a product page', 'Share an announcement'],
    verification: ['Link submission', 'Auto-check (X/IG/FB)', 'Screenshot'],
    web2_pitch: 'Amplify your message through real people',
    avg_completion_time: '2-5 min',
    fraud_risk: 'low',
  },
  [QUEST_TYPES.VISIT]: {
    label: 'Visit & Engage',
    icon: '🌐',
    description: 'Drive real traffic to your website',
    color: 'blue',
    examples: ['Visit a website', 'Spend 30 seconds on page', 'Scroll through landing page', 'Click a CTA'],
    verification: ['Unique tracking link', 'UTM + redirect', 'Screenshot'],
    web2_pitch: 'Direct traffic with SEO signals',
    avg_completion_time: '1-3 min',
    fraud_risk: 'low',
  },
  [QUEST_TYPES.FOLLOW]: {
    label: 'Follow / Subscribe',
    icon: '👥',
    description: 'Grow your audience organically',
    color: 'purple',
    examples: ['Follow on X / Instagram', 'Subscribe to newsletter', 'Join WhatsApp / Telegram', 'Follow Google Business'],
    verification: ['API check', 'Screenshot upload'],
    web2_pitch: 'Build your following with real users',
    avg_completion_time: '1-2 min',
    fraud_risk: 'low',
  },
  [QUEST_TYPES.REVIEW]: {
    label: 'Review & Feedback',
    icon: '⭐',
    description: 'Get authentic reviews and feedback',
    color: 'yellow',
    examples: ['Leave a Google review', 'Rate a product', 'Submit feedback form', 'Answer survey questions'],
    verification: ['Link submission', 'Screenshot', 'Form ID'],
    web2_pitch: 'Build trust with real reviews',
    avg_completion_time: '3-8 min',
    fraud_risk: 'medium', // Requires safeguards
    safeguards: ['One per account', 'Clear guidelines', 'No incentivized fake reviews'],
  },
  [QUEST_TYPES.REFERRAL]: {
    label: 'Referral',
    icon: '🔗',
    description: 'Word-of-mouth growth',
    color: 'green',
    examples: ['Share referral link', 'Invite 1-3 friends', 'Friend completes first action'],
    verification: ['Referral tracking', 'Conversion confirmation'],
    web2_pitch: 'Grow through trusted recommendations',
    avg_completion_time: '5-10 min',
    fraud_risk: 'medium', // Requires conversion tracking
    safeguards: ['Reward on conversion only', 'No instant token payout'],
  },
};

// ========== CAMPAIGN GOALS (Web2-Friendly) ==========
export const CAMPAIGN_GOALS = {
  TRAFFIC: { id: 'traffic', label: 'Traffic', icon: '🌐', description: 'Drive visitors to your website', recommended_types: [QUEST_TYPES.VISIT, QUEST_TYPES.SHARE] },
  AWARENESS: { id: 'awareness', label: 'Awareness', icon: '📣', description: 'Spread the word about your brand', recommended_types: [QUEST_TYPES.SHARE, QUEST_TYPES.FOLLOW] },
  FOLLOWERS: { id: 'followers', label: 'Followers', icon: '👥', description: 'Grow your social audience', recommended_types: [QUEST_TYPES.FOLLOW, QUEST_TYPES.SHARE] },
  REVIEWS: { id: 'reviews', label: 'Reviews', icon: '⭐', description: 'Collect authentic feedback', recommended_types: [QUEST_TYPES.REVIEW] },
  LEADS: { id: 'leads', label: 'Leads', icon: '📧', description: 'Capture potential customers', recommended_types: [QUEST_TYPES.VISIT, QUEST_TYPES.REFERRAL] },
};

// ========== VERIFICATION TYPES ==========
export const VERIFICATION_TYPES = {
  LINK: { id: 'link', label: 'Link Submission', description: 'User submits proof URL', auto: false },
  SCREENSHOT: { id: 'screenshot', label: 'Screenshot', description: 'User uploads screenshot proof', auto: false },
  TRACKING: { id: 'tracking', label: 'Tracking Link', description: 'Auto-verified via unique link', auto: true },
  API: { id: 'api', label: 'API Verification', description: 'Auto-verified via platform API', auto: true },
  SELF: { id: 'self', label: 'Self-Verify', description: 'User confirms completion', auto: false },
};

// ========== REWARD STRATEGY ==========
// Default: XP only. BURNZ only at milestones
export const QUEST_REWARDS = {
  base_xp_per_action: 100,     // Standard XP per quest action
  completion_bonus_xp: 50,     // Bonus for completing all tasks
  first_quest_bonus_xp: 100,   // First quest of the day bonus
  
  // NO direct BURNZ for quests
  burnz_reward: 0,
  
  // Only milestones unlock BURNZ
  milestones: {
    'quests_5': { xp_bonus: 250, badge: 'Quest Explorer' },
    'quests_10': { xp_bonus: 500, badge: 'Quest Master' },
    'quests_25': { xp_bonus: 1000, badge: 'Campaign Veteran' },
    'quests_50': { xp_bonus: 2500, badge: 'Marketing Legend' },
  },
};

// ========== CAMPAIGN PRICING (ADA Default) ==========
export const CAMPAIGN_PRICING = {
  STARTER: {
    id: 'starter',
    name: 'Starter',
    price_ada: 250,
    price_burnz: 250000, // 20% discount
    reach: '5K-15K',
    duration: '1 week',
    quests_included: 2,
    features: [
      'Up to 2 quest campaigns',
      '5K-15K user reach',
      'Basic reporting',
      'Email support',
    ],
  },
  GROWTH: {
    id: 'growth',
    name: 'Growth',
    price_ada: 750,
    price_burnz: 600000, // 25% discount
    reach: '25K-75K',
    duration: '2 weeks',
    quests_included: 5,
    popular: true,
    features: [
      'Up to 5 quest campaigns',
      '25K-75K user reach',
      'Advanced analytics',
      'Priority support',
      'Featured placement',
    ],
  },
  ENTERPRISE: {
    id: 'enterprise',
    name: 'Enterprise',
    price_ada: 2500,
    price_burnz: 1750000, // 30% discount
    reach: '150K+',
    duration: '4 weeks',
    quests_included: 15,
    features: [
      'Up to 15 quest campaigns',
      '150K+ user reach',
      'Real-time analytics',
      'Dedicated account manager',
      'Custom targeting',
      'Team sponsorship options',
    ],
  },
};

// ========== QUEST STRUCTURE (Standard Template) ==========
// Every quest follows this format:
// 1. What to do
// 2. Why it matters (for the brand)
// 3. How to verify
// 4. What you earn
export const QUEST_TEMPLATE = {
  // Required fields
  id: null,
  title: '',
  description: '',
  quest_type: QUEST_TYPES.SHARE,
  
  // Action details
  action_url: '',       // Where to perform the action
  action_label: '',     // CTA text
  
  // Verification
  verification_type: VERIFICATION_TYPES.SELF.id,
  verification_instructions: '',
  
  // Rewards (XP only by default)
  xp_reward: QUEST_REWARDS.base_xp_per_action,
  burnz_reward: 0,
  
  // Campaign info
  partner_id: null,
  partner_name: '',
  partner_logo: '',
  
  // Status
  status: 'active',
  completions_count: 0,
  max_completions: null, // Unlimited by default
  
  // Dates
  starts_at: null,
  expires_at: null,
};

// ========== WEB2 MODE (No Crypto Jargon) ==========
export const WEB2_MODE = {
  enabled: true,
  features: {
    no_wallet_required: true,
    ada_pricing_only: true,
    simple_reports: true,
    no_crypto_language: true,
  },
  language_map: {
    'BURNZ': 'reward points',
    'XP': 'engagement points',
    'wallet': 'account',
    'token': 'reward',
    'burn': 'redeem',
  },
};

// ========== SIMPLE REPORTS (What Web2 Clients Get) ==========
export const CAMPAIGN_REPORT_FIELDS = [
  { id: 'total_shares', label: 'Total Shares', description: 'Number of content shares' },
  { id: 'estimated_reach', label: 'Estimated Reach', description: 'Total potential impressions' },
  { id: 'clicks', label: 'Clicks / Visits', description: 'Traffic driven to your site' },
  { id: 'engagement_rate', label: 'Engagement Rate', description: 'Percentage of users who completed actions' },
  { id: 'completion_rate', label: 'Completion Rate', description: 'Quest completion percentage' },
];

// ========== DEMO QUESTS (New Model) ==========
export const DEMO_QUESTS = [
  // Onboarding Quest (Redesigned)
  {
    id: 'q0',
    title: 'Welcome to Burnz Union',
    description: 'Get started and earn your first rewards. Takes less than 3 minutes.',
    quest_type: QUEST_TYPES.FOLLOW,
    xp_reward: 450,
    burnz_reward: 0,
    status: 'active',
    completions_count: 45962,
    time_estimate: '5 min',
    featured: true,
    is_onboarding: true,
    partner_name: 'Burnz Union',
    tasks: [
      { id: 't0-0', title: 'Sign up UEX account', description: 'Create your UEX.us account for trading rewards', xp_reward: 100, action_url: 'https://uex.us/?ref=5jcsh81dhaos', action_label: 'Sign Up', verification_type: 'self' },
      { id: 't0-1', title: 'Create Your Account', description: 'Connect your wallet or sign up', xp_reward: 50, action_label: 'Connect', verification_type: 'self' },
      { id: 't0-2', title: 'Join Our Community', description: 'Join the Burnz Union Discord', xp_reward: 100, action_url: 'https://discord.gg/74wEdNQ5tC', action_label: 'Join Discord', verification_type: 'self' },
      { id: 't0-3', title: 'Follow on Twitter', description: 'Stay updated with announcements', xp_reward: 100, action_url: 'https://twitter.com/BurnzUnion', action_label: 'Follow', verification_type: 'self' },
      { id: 't0-4', title: 'Join a Team', description: 'Pick a recovery team to support', xp_reward: 100, action_url: '/app/teams', action_label: 'Browse Teams', verification_type: 'self' },
    ],
  },
  
  // Share & Amplify Quest
  {
    id: 'q1',
    title: 'Share Our Launch Announcement',
    description: 'Help spread the word about our new features by sharing on social media.',
    quest_type: QUEST_TYPES.SHARE,
    xp_reward: 200,
    burnz_reward: 0,
    status: 'active',
    completions_count: 2847,
    time_estimate: '2 min',
    partner_name: 'Burnz Union',
    partner_logo: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=100',
    tasks: [
      { id: 't1-1', title: 'Share on Twitter/X', description: 'Share the announcement with your followers', xp_reward: 100, action_url: 'https://twitter.com/intent/tweet?text=Check%20out%20@BurnzUnion%20-%20where%20rugged%20Web3%20users%20rebuild!', action_label: 'Share', verification_type: 'link' },
      { id: 't1-2', title: 'Tag a Friend', description: 'Mention someone who should know about this', xp_reward: 100, action_label: 'Done', verification_type: 'self' },
    ],
  },
  
  // Visit & Engage Quest (Web2 Favorite)
  {
    id: 'q2',
    title: 'Explore DeFi Protocol X',
    description: 'Visit our partner site and explore their new lending features.',
    quest_type: QUEST_TYPES.VISIT,
    xp_reward: 150,
    burnz_reward: 0,
    status: 'active',
    completions_count: 1523,
    time_estimate: '3 min',
    partner_id: 'p1',
    partner_name: 'DeFi Protocol X',
    partner_logo: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=100',
    tasks: [
      { id: 't2-1', title: 'Visit Landing Page', description: 'Check out the new features page', xp_reward: 50, action_url: 'https://example.com/features?utm_source=burnz', action_label: 'Visit', verification_type: 'tracking' },
      { id: 't2-2', title: 'Read the How It Works', description: 'Scroll through the explanation section', xp_reward: 50, action_label: 'Done', verification_type: 'self' },
      { id: 't2-3', title: 'Click Get Started', description: 'Explore the signup flow', xp_reward: 50, action_label: 'Done', verification_type: 'self' },
    ],
  },
  
  // Follow / Subscribe Quest
  {
    id: 'q3',
    title: 'Follow NFT Marketplace Y',
    description: 'Support our partner by following their social channels.',
    quest_type: QUEST_TYPES.FOLLOW,
    xp_reward: 200,
    burnz_reward: 0,
    status: 'active',
    completions_count: 3421,
    time_estimate: '2 min',
    partner_id: 'p2',
    partner_name: 'NFT Marketplace Y',
    partner_logo: 'https://images.unsplash.com/photo-1642104704074-907c0698cbd9?w=100',
    tasks: [
      { id: 't3-1', title: 'Follow on Twitter', description: 'Follow @NFTMarketplaceY', xp_reward: 100, action_url: 'https://twitter.com/NFTMarketplaceY', action_label: 'Follow', verification_type: 'self' },
      { id: 't3-2', title: 'Subscribe to Newsletter', description: 'Get weekly updates', xp_reward: 100, action_url: 'https://example.com/newsletter', action_label: 'Subscribe', verification_type: 'self' },
    ],
  },
  
  // Review & Feedback Quest (High Value for Web2)
  {
    id: 'q4',
    title: 'Review GameFi Studio Z',
    description: 'Share your honest feedback about their new game.',
    quest_type: QUEST_TYPES.REVIEW,
    xp_reward: 300,
    burnz_reward: 0,
    status: 'active',
    completions_count: 456,
    time_estimate: '5 min',
    partner_id: 'p3',
    partner_name: 'GameFi Studio Z',
    partner_logo: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=100',
    tasks: [
      { id: 't4-1', title: 'Play the Demo', description: 'Try the game for at least 10 minutes', xp_reward: 100, action_url: 'https://example.com/game', action_label: 'Play', verification_type: 'self' },
      { id: 't4-2', title: 'Submit Feedback', description: 'Answer 3 quick questions about your experience', xp_reward: 100, action_url: 'https://example.com/feedback', action_label: 'Give Feedback', verification_type: 'link' },
      { id: 't4-3', title: 'Rate on App Store', description: 'Leave an honest rating (optional)', xp_reward: 100, action_label: 'Rate', verification_type: 'self' },
    ],
  },
  
  // Referral Quest
  {
    id: 'q5',
    title: 'Invite Friends to Burnz Union',
    description: 'Grow our community by inviting friends who want a fresh start.',
    quest_type: QUEST_TYPES.REFERRAL,
    xp_reward: 500,
    burnz_reward: 0,
    status: 'active',
    completions_count: 892,
    time_estimate: '5 min',
    partner_name: 'Burnz Union',
    tasks: [
      { id: 't5-1', title: 'Copy Your Referral Link', description: 'Get your unique invite link', xp_reward: 50, action_url: '/app/profile?tab=referral', action_label: 'Get Link', verification_type: 'self' },
      { id: 't5-2', title: 'Share with 3 Friends', description: 'Send to friends who might be interested', xp_reward: 150, action_label: 'Shared', verification_type: 'self' },
      { id: 't5-3', title: 'Friend Joins', description: 'Earn bonus when a friend signs up', xp_reward: 300, action_label: 'Waiting...', verification_type: 'api' },
    ],
  },
];

// ========== CAMPAIGN BUILDER STEPS (Web2-Friendly) ==========
export const CAMPAIGN_BUILDER_STEPS = [
  {
    step: 1,
    title: 'Choose Your Goal',
    description: 'What do you want to achieve?',
    options: Object.values(CAMPAIGN_GOALS),
  },
  {
    step: 2,
    title: 'Choose Action Type',
    description: 'What should users do?',
    options: Object.values(QUEST_TYPE_CONFIG),
  },
  {
    step: 3,
    title: 'Set Budget',
    description: 'How much do you want to spend?',
    options: Object.values(CAMPAIGN_PRICING),
  },
  {
    step: 4,
    title: 'Review & Launch',
    description: 'Confirm your campaign details',
  },
];

// ========== PARTNER-FACING COPY (No Crypto Jargon) ==========
export const WEB2_MARKETING_COPY = {
  headline: 'Real Users. Verified Actions. Measurable Results.',
  subheadline: 'We let real users share your brand, visit your website, and talk about you — and you only pay for verified actions.',
  cta: 'Launch Your Campaign',
  benefits: [
    { icon: '📊', title: 'Measurable ROI', description: 'Track shares, visits, clicks, and conversions' },
    { icon: '✓', title: 'Verified Actions', description: 'Only pay for completed, verified tasks' },
    { icon: '👥', title: 'Real Users', description: '45K+ engaged community members' },
    { icon: '⚡', title: 'Quick Setup', description: 'Launch campaigns in under 5 minutes' },
  ],
};
